// contexts/TwgoldAuthContext.js
import React, { createContext, useState, useEffect, useCallback } from "react";
import api from "./axiosConfig";

const TwgoldAuthContext = createContext();

export const TwgoldAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  /* ===========================
     FETCH PROFILE
  =========================== */
  const fetchUserProfile = useCallback(async () => {
    try {
      const res = await api.get("/twgoldlogin/profile");

      if (res.data?.success && res.data?.data?.user) {
        setUser(res.data.data.user);
        return { success: true, user: res.data.data.user };
      }

      return { success: false };
    } catch (err) {
      console.error("[PROFILE ERROR]", err);
      return { success: false };
    }
  }, []);

  /* ===========================
     CHECK AUTH (NO TOKEN CHECK)
  =========================== */
  const checkAuthStatus = useCallback(async () => {
    try {
      const result = await fetchUserProfile();

      if (result.success) {
        return { success: true };
      }

      setUser(null);
      return { success: false };
    } catch (error) {
      console.error("[AUTH CHECK ERROR]", error);
      setUser(null);
      return { success: false };
    }
  }, [fetchUserProfile]);

  /* ===========================
     INIT
  =========================== */
  useEffect(() => {
    const init = async () => {
      try {
        // 🔥 DO NOT run if user already exists (IMPORTANT)
        if (user) {
          setLoading(false);
          setIsInitialized(true);
          return;
        }
  
        const result = await fetchUserProfile();
  
        if (!result.success) {
          setUser(null);
        }
  
      } catch (e) {
        setUser(null);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };
  
    init();
  }, [fetchUserProfile, user]);

  /* ===========================
     LOGIN
  =========================== */
  const twgold_login = async (email, password) => {
    try {
      setLoading(true);
  
      // Step 1: login (sets cookie)
      await api.post("/twgoldlogin/login", {
        email,
        password,
      });
  
      // 🔥 Step 2: ALWAYS fetch fresh profile
      const profile = await fetchUserProfile();
  
      if (!profile.success) {
        return {
          success: false,
          message: "Unable to fetch user after login",
        };
      }
  
      // 🔥 Step 3: ensure user is set BEFORE navigation
      setUser(profile.user);
  
      return {
        success: true,
        user: profile.user,
        message: "Login successful",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Login failed. Please check credentials.",
      };
    } finally {
      setLoading(false);
    }
  };
  /* ===========================
     LOGOUT (NO MANUAL CLEAR)
  =========================== */
  const twgold_logout = async () => {
    try {
      await api.post("/twgoldlogin/logout");
    } catch (err) {
      console.warn("Logout error:", err);
    } finally {
      setUser(null);
      window.location.href = "/twgl&articles/login";
    }
  };

  /* ===========================
     CONTEXT VALUE
  =========================== */
  const value = {
    user,
    loading,
    isInitialized,
    isAuthenticated: !!user,
    twgold_login,
    twgold_logout,
    checkAuthStatus,
    fetchUserProfile,
  };

  return (
    <TwgoldAuthContext.Provider value={value}>
      {children}
    </TwgoldAuthContext.Provider>
  );
};

export default TwgoldAuthContext;